import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";

/**
 * Animated infinite-scroll sponsors banner.
 * Renders only when at least one active sponsor exists.
 * Placed just above <Footer /> on public pages.
 */
export default function SponsorsBanner() {
  const { lang } = useI18n();
  const [sponsors, setSponsors] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get("/sponsors")
      .then((r) => {
        if (!alive) return;
        setSponsors(Array.isArray(r.data) ? r.data : []);
      })
      .catch(() => {})
      .finally(() => alive && setLoaded(true));
    return () => { alive = false; };
  }, []);

  if (!loaded || sponsors.length === 0) return null;

  // Duplicate the list so the CSS marquee loop is seamless
  const looped = [...sponsors, ...sponsors];

  return (
    <section
      data-testid="sponsors-banner"
      className="border-t border-white/5 bg-[#0E0C0B] py-12 overflow-hidden"
      aria-label={lang === "en" ? "Sponsors" : "Sponsors"}
    >
      <div className="max-w-[1400px] mx-auto px-6">
        <div className="overline text-center mb-8 text-[#A8A39D] tracking-[0.4em]">
          {lang === "en" ? "With the support of" : "Avec le soutien de"}
        </div>
        <div className="sponsors-marquee">
          <div className="sponsors-track">
            {looped.map((s, i) => {
              const Tag = s.link_url ? "a" : "div";
              const tagProps = s.link_url
                ? { href: s.link_url, target: "_blank", rel: "sponsored noopener noreferrer" }
                : {};
              return (
                <Tag
                  key={`${s.id}-${i}`}
                  data-testid={`sponsor-item-${s.id}`}
                  {...tagProps}
                  className="sponsors-item shrink-0 px-10 flex items-center justify-center group"
                  aria-label={s.alt || s.name}
                  title={s.name}
                >
                  <img
                    src={s.logo_url}
                    alt={s.alt || s.name}
                    loading="lazy"
                    decoding="async"
                    className="max-h-12 md:max-h-14 w-auto object-contain opacity-60 grayscale transition-all duration-500 group-hover:opacity-100 group-hover:grayscale-0"
                  />
                </Tag>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
